import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Anchored popover.
 *
 * Base for the select, the date picker and the menus. Closes on an outside
 * click or Escape.
 *
 * Drawn at the end of the document and positioned against its trigger, rather
 * than inside the trigger's own box. Inside, it was clipped by any ancestor
 * that scrolls -- a drawer body, a settings card, a table -- so a driver list
 * in the vehicle form showed its first two names and a hard edge. Out here
 * nothing can crop it. It opens below the trigger, flips above when there is
 * more room there, and scrolls itself when even that is not enough.
 */
export function Popover({
  open,
  onClose,
  align = 'left',
  className,
  role = 'dialog',
  children,
}: {
  open: boolean;
  onClose: () => void;
  align?: 'left' | 'right';
  /** Extra classes on the floating box, e.g. `menu-pop`. */
  className?: string;
  role?: string;
  children: ReactNode;
}) {
  /* Stays where the popover is declared, so the trigger can be found from it. */
  const marker = useRef<HTMLSpanElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', top: 0, left: 0, visibility: 'hidden' });

  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = marker.current?.parentElement;
      const box = ref.current;
      if (!anchor || !box) return;
      const a = anchor.getBoundingClientRect();
      const gap = 6;
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = Math.max(box.offsetWidth, a.width);
      const natural = box.scrollHeight;
      const below = vh - a.bottom - gap - margin;
      const above = a.top - gap - margin;
      const up = natural > below && above > below;
      const room = Math.max(140, up ? above : below);
      const left = Math.min(Math.max(margin, align === 'right' ? a.right - width : a.left), vw - width - margin);
      setStyle({
        position: 'fixed',
        left: Math.max(margin, left),
        right: 'auto',
        top: up ? 'auto' : a.bottom + gap,
        bottom: up ? vh - a.top + gap : 'auto',
        /* Never narrower than its own stylesheet minimum, nor than the trigger. */
        minWidth: width,
        maxHeight: room,
        overflowY: natural > room ? 'auto' : undefined,
      });
    };
    place();
    /* Second pass once the content has its real size (fonts, images). */
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    /* Capture: a scroll in any container moves the trigger. */
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      setStyle({ position: 'fixed', top: 0, left: 0, visibility: 'hidden' });
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target)) return;
      /* The trigger toggles; clicking it must not close-then-reopen. */
      if (marker.current?.parentElement?.contains(target)) return;
      closeRef.current();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeRef.current();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <>
      <span ref={marker} hidden />
      {open
        ? createPortal(
            <div
              ref={ref}
              className={`pop pop-floating${className ? ` ${className}` : ''}`}
              role={role}
              style={style}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
